import { FormField, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@volee/ui'
import {
  useController,
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form'

// A Radix Select bound to react-hook-form. Four forms needed it and three had written their
// own binding, one of which carried a defect the other two shared — see the README.

// Radix refuses an empty string as an item value, so "nothing chosen" travels as this.
const CLEARED = '__cleared__'

export interface SelectFieldProps<TFields extends FieldValues, TValue extends string | number> {
  readonly form: UseFormReturn<TFields>
  readonly name: FieldPath<TFields>
  readonly label: string
  readonly description?: string
  readonly placeholder?: string
  readonly error?: string
  /** `disabled`: offered but out of reach, the reason said by `description` — never hidden, so the
   *  reader learns it exists and what would turn it on. */
  readonly options: readonly { value: TValue; label: string; disabled?: boolean }[]
  readonly pending: boolean
  readonly required?: boolean
  /** A value that is not this form's to rewrite. Radix has no `readOnly`, so it disables —
   *  the trigger still shows the choice. */
  readonly locked?: boolean
  /** Offers a row that clears the field. Radix refuses `''` as a value, hence a sentinel. */
  readonly clearLabel?: string
  /** Called after the field has taken the value, for a select another control depends on:
   *  picking a home side has to drop the ground chosen for the previous one. */
  readonly onChosen?: (value: string) => void
}

/** `useController` and not `setValue`, and generic over the option's type: the two reasons
 * are in apps/admin-panel/CLAUDE.md, and one of them was a defect three forms shared. */
export function SelectField<TFields extends FieldValues, TValue extends string | number>({
  form,
  name,
  label,
  description,
  placeholder,
  error,
  options,
  pending,
  required,
  locked,
  clearLabel,
  onChosen,
}: SelectFieldProps<TFields, TValue>) {
  const { field } = useController({ control: form.control, name })

  function choose(raw: string) {
    if (raw === CLEARED) {
      field.onChange('')
      form.clearErrors(name)
      onChosen?.('')
      return
    }
    const chosen = options.find((option) => String(option.value) === raw)
    if (chosen === undefined) return
    field.onChange(chosen.value)
    // A blocked submit otherwise leaves the field red over a choice just corrected.
    form.clearErrors(name)
    onChosen?.(String(chosen.value))
  }

  return (
    <FormField label={label} description={description} error={error} required={required}>
      {(props) => (
        <Select
          value={String(field.value ?? '')}
          onValueChange={choose}
          disabled={pending || locked}
        >
          <SelectTrigger {...props} onBlur={field.onBlur} ref={field.ref}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {clearLabel === undefined ? null : (
              <SelectItem value={CLEARED}>{clearLabel}</SelectItem>
            )}
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={String(option.value)}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </FormField>
  )
}
